import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSocketEvents } from '../hooks/useSocket';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Globe, LogOut, Users, FileText, TrendingUp, CheckCircle, Clock, AlertCircle, UserCheck, Building2, Search, Filter, Plus, UserPlus, MessageCircle, User, Lock, Settings, Briefcase } from 'lucide-react';
import MyProspects from '../components/MyProspects';
import ChatWidget from '../components/ChatWidget';
import NotificationBell from '../components/NotificationBell';
import SearchAndSort from '../components/SearchAndSort';
import AloriaLogo from '../components/AloriaLogo';
import ProfileSettings from '../components/ProfileSettings';
import CredentialsPopup from '../components/CredentialsPopup';
import useSocket from '../hooks/useSocket';
import api, { clientsAPI, casesAPI, dashboardAPI, employeesAPI, visitorsAPI } from '../utils/api';
import { formatApiErrorMessage } from '../utils/errors';

// Nombre d'étapes par type de procédure (correspond aux workflows backend)
const WORKFLOW_STEP_COUNTS = {
  "Canada": {
    "Permis de travail": 10,
    "Permis d'études": 10,
    "Résidence permanente (EE/Arrima)": 10
  },
  "France": {
    "Permis de travail (Passeport Talent)": 10,
    "Visa étudiant": 10,
    "Regroupement familial": 10
  },
  "Belgique": {
    "Permis Unique / Travail Salarié (incluant Carte Bleue Européenne)": 10,
    "Visa D Étudiant": 10,
    "Regroupement Familial": 10,
    "Carte Professionnelle (Travailleur Indépendant)": 10
  }
};

const DEFAULT_TOTAL_STEPS = 10;

/**
 * Retourne le nombre total d'étapes pour un dossier.
 * Utilise workflow_steps.length si disponible, sinon la table WORKFLOW_STEP_COUNTS, sinon 10.
 */
function getTotalSteps(caseData, country, visaType) {
  if (caseData?.workflow_steps?.length > 0) return caseData.workflow_steps.length;
  return WORKFLOW_STEP_COUNTS[country]?.[visaType] || DEFAULT_TOTAL_STEPS;
}

export default function ManagerDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [myClients, setMyClients] = useState([]);
  const [filteredMyClients, setFilteredMyClients] = useState([]);

  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newVisitor, setNewVisitor] = useState({ 
    full_name: '',
    phone_number: '',
    purpose: 'Consultation initiale',
    other_purpose: '',
    cni_number: '' 
  });
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateEmployee, setShowCreateEmployee] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [newClientCredentials, setNewClientCredentials] = useState(null);
  const [newClient, setNewClient] = useState({
    email: '',
    full_name: '',
    phone: '',
    country: '',
    visa_type: '',
    first_payment_amount: 0,
    message: ''
  });
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    full_name: '',
    phone: ''
  });
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const [reassignDialog, setReassignDialog] = useState({ show: false, client: null, newEmployeeId: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileSettings, setShowProfileSettings] = useState(false);

  
  // WebSocket hook
  const { connected } = useSocket(localStorage.getItem('token'));

  // Calcul dynamique des stats à partir des données locales pour mise à jour en temps réel
  const calculatedStats = React.useMemo(() => {
    const activeCases = cases.filter(c => 
      c.status === 'In Progress' || c.status === 'Under Review' || c.status === 'En cours' || c.status === 'En attente'
    ).length;
    
    const completedCases = cases.filter(c => 
      c.status === 'Approved' || c.status === 'Completed' || c.status === 'Terminé' || c.status === 'Approuvé'
    ).length;
    
    return {
      total_cases: cases.length,
      active_cases: activeCases,
      completed_cases: completedCases,
      total_clients: clients.length,
      total_employees: employees.length,
      total_visitors: visitors.length
    };
  }, [cases, clients, employees, visitors]);

  // Utiliser les stats calculées si disponibles, sinon les stats du backend
  const displayStats = calculatedStats.total_cases > 0 ? calculatedStats : stats;

  useEffect(() => {
    fetchData();

    // Supprimé: Auto-refresh automatique qui causait des pertes de données
    // Les données seront mises à jour uniquement après les actions utilisateur

  }, []);

  // Mettre à jour myClients quand clients change
  useEffect(() => {
    if (user && clients.length > 0) {
      setMyClients(clients.filter(c => c.assigned_employee_id === user.id));
    }
  }, [clients, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, clientsRes, casesRes, employeesRes, visitorsRes] = await Promise.all([
        dashboardAPI.getStats(),
        clientsAPI.getAll(),
        casesAPI.getAll(),
        employeesAPI.getAll(),
        visitorsAPI.getAll()
      ]);

      setStats(statsRes.data);
      setClients(clientsRes.data);
      setCases(casesRes.data);
      setEmployees(employeesRes.data);
      setVisitors(visitorsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fonctions de refresh manuel pour mettre à jour les données après les actions
  const refreshData = () => {
    fetchData();
  };

  // Écouter les mises à jour de clients en temps réel
  useSocketEvents('clientUpdated', (update) => {
    console.log('Client updated via Socket.IO:', update);
    refreshData();
  });

  const handleReassignClient = async () => {
    if (!reassignDialog.newEmployeeId) {
      toast.error('Veuillez sélectionner un employé');
      return;
    }

    try {
      await clientsAPI.reassign(reassignDialog.client.id, reassignDialog.newEmployeeId);
      toast.success('Client réassigné avec succès');
      setReassignDialog({ show: false, client: null, newEmployeeId: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorMessage(error, 'Erreur lors de la réassignation'));
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    if (!newClient.email || !newClient.full_name || !newClient.country || !newClient.visa_type) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      // Utiliser l'endpoint refactorisé /api/clients
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newClient)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(formatApiErrorMessage(errorData, 'Erreur lors de la création'));
      }
      
      const data = await response.json();
      
      // Afficher confirmation sans exposer le mot de passe
      toast.success(
        <div>
          <p className="font-semibold">✅ Client créé avec succès!</p>
          <div className="mt-2 text-sm">
            <p>📧 Email: {data.login_email || data.email}</p>
            <p className="mt-1 text-green-400">🔐 Identifiants envoyés par email</p>
          </div>
        </div>,
        { duration: 8000 }
      );
      
      setShowCreateClient(false);
      
      // Reset form
      setNewClient({
        email: '',
        full_name: '',
        phone: '',
        country: '',
        visa_type: '',
        first_payment_amount: 0,
        message: ''
      });
      
      fetchData(); // Refresh
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création du client');
      console.error(error);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    if (!newEmployee.email || !newEmployee.full_name || !newEmployee.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      // Utiliser l'endpoint /api/users/create
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: newEmployee.email,
          full_name: newEmployee.full_name,
          phone: newEmployee.phone,
          role: 'EMPLOYEE',
          send_email: true  // TOUJOURS envoyer par email pour la sécurité
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(formatApiErrorMessage(errorData, 'Erreur lors de la création'));
      }
      
      const data = await response.json();
      
      // Afficher confirmation sans exposer le mot de passe
      toast.success(
        <div>
          <p className="font-semibold">✅ Employé créé avec succès!</p>
          <div className="mt-2 text-sm">
            <p>📧 Email: {data.email}</p>
            <p className="mt-1 text-green-400">🔐 Identifiants envoyés par email</p>
          </div>
        </div>,
        { duration: 8000 }
      );
      
      setShowCreateEmployee(false);
      
      // Reset form
      setNewEmployee({
        email: '',
        full_name: '',
        phone: ''
      });
      
      fetchData(); // Refresh
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création de l\'employé');
      console.error(error);
    }
  };

  const handleUpdateCase = async (caseId, updates) => {
    try {
      await casesAPI.update(caseId, updates);
      toast.success('Dossier mis à jour');
      fetchData(); // Refresh unique
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      console.error(error);
    }
  };

  const handleAddVisitor = async () => {
    if (!newVisitor.full_name || !newVisitor.phone_number || !newVisitor.cni_number) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await visitorsAPI.create(newVisitor);
      toast.success('Visiteur enregistré avec succès');
      setNewVisitor({
        full_name: '',
        phone_number: '',
        purpose: 'Consultation initiale',
        other_purpose: '',
        cni_number: ''
      });
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error creating visitor:', error);
      toast.error('Erreur lors de l\'enregistrement du visiteur');
    }
  };

  const handleCheckoutVisitor = async (visitorId) => {
    try {
      await visitorsAPI.checkout(visitorId);
      toast.success('Visiteur marqué comme parti');
      fetchData();
    } catch (error) {
      console.error('Error checking out visitor:', error);
      toast.error('Erreur lors du checkout');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Nouveau': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'En cours': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      'En attente': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'Approuvé': 'bg-green-500/10 text-green-400 border-green-500/20',
      'Terminé': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      'In Progress': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      'Under Review': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'Approved': 'bg-green-500/10 text-green-400 border-green-500/20',
      'Completed': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    return colors[status] || 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <Card className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] border-none shadow-2xl rounded-none">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <AloriaLogo className="h-10" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white">
                  Manager Dashboard
                </h1>
                <p className="text-sm text-slate-400 mt-1">Bienvenue, {user?.full_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowProfileSettings(!showProfileSettings)}
                className="bg-slate-700 hover:bg-slate-600 text-white"
                title="Mon Profil"
              >
                <Settings className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Profil</span>
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/50" data-testid="create-client-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Nouveau Client</span>
                    <span className="sm:hidden">Client</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-white">Créer un Nouveau Client</DialogTitle>
                    <DialogDescription className="text-slate-400">
                      Remplissez les informations pour créer un nouveau profil client et son dossier d'immigration
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateClient} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Nom Complet *</Label>
                        <Input
                          value={newClient.full_name}
                          onChange={(e) => setNewClient({ ...newClient, full_name: e.target.value })}
                          required
                          placeholder="Ex: Jean Dupont"
                          className="bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Email *</Label>
                        <Input
                          type="email"
                          value={newClient.email}
                          onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                          required
                          placeholder="email@example.com"
                          className="bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Téléphone</Label>
                      <Input
                        value={newClient.phone}
                        onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                        placeholder="+237 XXX XXX XXX"
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Pays de Destination *</Label>
                        <select
                          value={newClient.country}
                          onChange={(e) => setNewClient({ ...newClient, country: e.target.value, visa_type: '' })}
                          required
                          className="w-full bg-slate-800 border-slate-600 text-white p-2 rounded"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="Canada">Canada</option>
                          <option value="France">France</option>
                          <option value="Belgique">Belgique</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label className="text-slate-300">Type de Visa *</Label>
                        <select
                          value={newClient.visa_type}
                          onChange={(e) => setNewClient({ ...newClient, visa_type: e.target.value })}
                          required
                          disabled={!newClient.country}
                          className="w-full bg-slate-800 border-slate-600 text-white p-2 rounded"
                        >
                          <option value="">Sélectionner...</option>
                          {newClient.country === 'Canada' && (
                            <>
                              <option value="Permis de travail">Permis de travail</option>
                              <option value="Permis d'études">Permis d'études</option>
                              <option value="Résidence permanente (EE/Arrima)">Résidence permanente (EE/Arrima)</option>
                            </>
                          )}
                          {newClient.country === 'France' && (
                            <>
                              <option value="Permis de travail (Passeport Talent)">Permis de travail (Passeport Talent)</option>
                              <option value="Visa étudiant">Visa étudiant</option>
                              <option value="Regroupement familial">Regroupement familial</option>
                            </>
                          )}
                          {newClient.country === 'Belgique' && (
                            <>
                              <option value="Permis Unique / Travail Salarié (incluant Carte Bleue Européenne)">Permis Unique / Travail Salarié (incluant Carte Bleue Européenne)</option>
                              <option value="Visa D Étudiant">Visa D Étudiant</option>
                              <option value="Regroupement Familial">Regroupement Familial</option>
                              <option value="Carte Professionnelle (Travailleur Indépendant)">Carte Professionnelle (Travailleur Indépendant)</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Premier Versement (CFA)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newClient.first_payment_amount}
                        onChange={(e) => setNewClient({ ...newClient, first_payment_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="Montant du premier versement (optionnel)"
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                      <p className="text-slate-500 text-xs mt-1">Optionnel - Si le client effectue déjà un premier versement</p>
                    </div>
                    
                    <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg">
                      <p className="text-blue-400 text-sm">
                        ℹ️ Un email sera automatiquement envoyé au client avec ses identifiants de connexion sécurisés.
                      </p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                      disabled={!newClient.country || !newClient.visa_type}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Créer le Client
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              <NotificationBell />
              <Button 
                onClick={logout}
                className="bg-slate-700 hover:bg-slate-600 text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profil Settings */}
      {showProfileSettings && (
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Mon Profil</h2>
              <Button
                onClick={() => setShowProfileSettings(false)}
                variant="outline"
                className="border-slate-600 text-white"
              >
                Retour
              </Button>
            </div>
            <ProfileSettings user={user} onUpdate={fetchData} />
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {!showProfileSettings && (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-blue-500 border-slate-700" data-testid="kpi-total-cases">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Total Dossiers</p>
                  <p className="text-3xl font-bold text-white">{displayStats?.total_cases || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center border border-blue-500/20">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-yellow-500 border-slate-700" data-testid="kpi-active-cases">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Dossiers Actifs</p>
                  <p className="text-3xl font-bold text-white">{displayStats?.active_cases || 0}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center border border-yellow-500/20">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-green-500 border-slate-700" data-testid="kpi-completed-cases">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Terminés</p>
                  <p className="text-3xl font-bold text-white">{displayStats?.completed_cases || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center border border-green-500/20">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-orange-500 border-slate-700" data-testid="kpi-total-clients">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Total Clients</p>
                  <p className="text-3xl font-bold text-white">{displayStats?.total_clients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center border border-orange-500/20">
                  <Users className="w-6 h-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs - Mobile Optimized */}
        <Tabs defaultValue="clients" className="space-y-5 sm:space-y-6">
          {/* Mobile: Scrollable Tabs, Desktop: Grid */}
          <div className="-mx-4 sm:mx-0">
            <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-5 bg-[#1E293B] border border-slate-700 p-1.5 sm:p-2 gap-1 sm:gap-2 scrollbar-hide">
              <TabsTrigger value="clients" className="flex-shrink-0 min-w-[100px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all touch-manipulation">👥 Clients</TabsTrigger>
              <TabsTrigger value="employees" className="flex-shrink-0 min-w-[100px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all touch-manipulation">👔 Équipe</TabsTrigger>
              <TabsTrigger value="cases" className="flex-shrink-0 min-w-[100px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all touch-manipulation">📁 Dossiers</TabsTrigger>
              <TabsTrigger value="visitors" className="flex-shrink-0 min-w-[100px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all touch-manipulation">🚶 Visiteurs</TabsTrigger>
              <TabsTrigger value="prospects" className="flex-shrink-0 min-w-[100px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all touch-manipulation">🎯 Prospects</TabsTrigger>
            </TabsList>
          </div>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <Tabs defaultValue="all-clients" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 bg-[#0F172A] border border-slate-700">
                <TabsTrigger 
                  value="all-clients" 
                  className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300"
                >
                  Tous les Clients
                </TabsTrigger>
                <TabsTrigger 
                  value="my-clients" 
                  className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300"
                >
                  Mes Clients
                </TabsTrigger>
              </TabsList>

              {/* Tous les Clients */}
              <TabsContent value="all-clients">
                <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Liste de Tous les Clients</CardTitle>
                    <CardDescription className="text-slate-400">Suivez l'évolution des dossiers de vos clients</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Recherche et Tri */}
                    <SearchAndSort
                      data={clients}
                      searchFields={['client_name', 'assigned_employee_name', 'country', 'visa_type', 'current_status']}
                      sortOptions={[
                        { value: 'created_at', label: 'Date de création' },
                        { value: 'client_name', label: 'Nom du client' },
                        { value: 'country', label: 'Pays' },
                        { value: 'progress_percentage', label: 'Progression' },
                        { value: 'current_status', label: 'Statut' }
                      ]}
                      onFilteredDataChange={setFilteredClients}
                      placeholder="Rechercher un dossier (client, pays, type de visa, statut)..."
                    />

                {/* Cartes Clients - Style Compact */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {filteredClients.map((client) => {
                    const clientCase = cases.find(c => c.client_id === client.user_id);
                    const currentStepIndex = clientCase?.current_step_index || 0;
                    const totalSteps = getTotalSteps(clientCase, client.country, client.visa_type);
                    const safePercentage = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

                    return (
                      <Card key={client.id} className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-2 border-slate-700 hover:border-orange-500 transition-all">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center border border-orange-500/20">
                              <User className="w-5 h-5 text-orange-400" />
                            </div>
                            <Badge className={getStatusColor(client.current_status)}>
                              {client.current_status || 'Nouveau'}
                            </Badge>
                          </div>
                          <h3 className="font-bold text-lg text-white mb-1">
                            {client.full_name || client.client_name || 'Client'}
                          </h3>
                          <p className="text-sm text-slate-400 mb-3">
                            {client.country} - {client.visa_type}
                          </p>

                          {/* Progression */}
                          <div className="mb-3">
                            <div className="flex justify-between items-center text-xs text-slate-400 mb-1">
                              <span>Étape {currentStepIndex + 1} sur {totalSteps}</span>
                              <span className="font-medium text-orange-400">{safePercentage}%</span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-orange-500 to-orange-600 h-full transition-all" 
                                style={{ width: `${safePercentage}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2 mb-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Conseiller:</span>
                              <span className="font-semibold text-white text-xs">{client.assigned_employee_name || 'Non assigné'}</span>
                            </div>
                            {clientCase && (
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">Étape actuelle:</span>
                                <span className="font-semibold text-slate-300 text-xs truncate ml-2">{clientCase.workflow_steps?.[currentStepIndex]?.title || '—'}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-xs"
                              onClick={() => setSelectedClient(client)}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              Détails
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white text-xs"
                              onClick={() => setReassignDialog({ show: true, client, newEmployeeId: '' })}
                            >
                              Réassigner
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {filteredClients.length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <p className="text-slate-400 text-lg">Aucun client trouvé</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mes Clients (Manager's own clients) */}
          <TabsContent value="my-clients">
            <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Mes Clients Personnels</CardTitle>
                <CardDescription className="text-slate-400">Suivez l'évolution des dossiers de vos clients personnels</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Recherche et Tri */}
                <SearchAndSort
                  data={myClients}
                  searchFields={['full_name', 'client_name', 'country', 'visa_type', 'current_status']}
                  sortOptions={[
                    { value: 'created_at', label: 'Date de création' },
                    { value: 'full_name', label: 'Nom' },
                    { value: 'country', label: 'Pays' },
                    { value: 'progress_percentage', label: 'Progression' }
                  ]}
                  onFilteredDataChange={setFilteredMyClients}
                  placeholder="Rechercher dans mes clients..."
                />

                {/* Cartes Clients - Style Compact */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {filteredMyClients.map((client) => {
                    const clientCase = cases.find(c => c.client_id === client.user_id);
                    const currentStepIndex = clientCase?.current_step_index || 0;
                    const totalSteps = getTotalSteps(clientCase, client.country, client.visa_type);
                    const safePercentage = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

                    return (
                      <Card key={client.id} className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-2 border-slate-700 hover:border-orange-500 transition-all">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center border border-orange-500/20">
                              <User className="w-5 h-5 text-orange-400" />
                            </div>
                            <Badge className={getStatusColor(client.current_status)}>
                              {client.current_status || 'Nouveau'}
                            </Badge>
                          </div>
                          <h3 className="font-bold text-lg text-white mb-1">
                            {client.full_name || client.client_name || 'Client'}
                          </h3>
                          <p className="text-sm text-slate-400 mb-3">
                            {client.country} - {client.visa_type}
                          </p>

                          {/* Progression */}
                          <div className="mb-3">
                            <div className="flex justify-between items-center text-xs text-slate-400 mb-1">
                              <span>Étape {currentStepIndex + 1} sur {totalSteps}</span>
                              <span className="font-medium text-orange-400">{safePercentage}%</span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-orange-500 to-orange-600 h-full transition-all" 
                                style={{ width: `${safePercentage}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2 mb-3">
                            {clientCase && (
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">Étape actuelle:</span>
                                <span className="font-semibold text-slate-300 text-xs truncate ml-2">{clientCase.workflow_steps?.[currentStepIndex]?.title || '—'}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-xs"
                              onClick={() => setSelectedClient(client)}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              Détails
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white text-xs"
                              onClick={() => setReassignDialog({ show: true, client, newEmployeeId: '' })}
                            >
                              Réassigner
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {clients.filter(c => c.assigned_employee_id === user?.id).length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <p className="text-slate-400 text-lg">Aucun client ne vous est directement assigné</p>
                      <p className="text-slate-500 text-sm mt-2">Créez un nouveau client ou demandez une réassignation</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </TabsContent>

      {/* Employees Tab */}
          <TabsContent value="employees">
            <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Membres de l'Équipe</CardTitle>
                  <CardDescription className="text-slate-400">Voir et gérer vos conseillers en immigration</CardDescription>
                </div>
                <Button
                  onClick={() => setShowCreateEmployee(true)}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer un Employé
                </Button>
              </CardHeader>
              <CardContent>
                {/* Recherche et Tri */}
                <SearchAndSort
                  data={employees}
                  searchFields={['full_name', 'email', 'phone']}
                  sortOptions={[
                    { value: 'full_name', label: 'Nom' },
                    { value: 'email', label: 'Email' },
                    { value: 'created_at', label: 'Date d\'ajout' }
                  ]}
                  onFilteredDataChange={setFilteredEmployees}
                  placeholder="Rechercher un employé (nom, email, téléphone)..."
                />

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredEmployees.map((employee) => {
                    const employeeCases = cases.filter(c => {
                      const client = clients.find(cl => cl.id === c.client_id);
                      return client?.assigned_employee_id === employee.id;
                    });
                    const completedCases = employeeCases.filter(c => c.status === 'Approved' || c.status === 'Completed').length;
                    const activeCases = employeeCases.filter(c => c.status === 'In Progress' || c.status === 'Under Review').length;

                    return (
                      <Card key={employee.id} className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-2 border-slate-700 hover:border-orange-500 transition-all">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-12 h-12 bg-orange-500/10 rounded-full flex items-center justify-center border border-orange-500/20">
                              <UserCheck className="w-6 h-6 text-orange-400" />
                            </div>
                            <Badge variant={employee.is_active ? "success" : "secondary"} className={employee.is_active ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-slate-700 text-slate-400"}>
                              {employee.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                          </div>
                          <h3 className="font-bold text-lg text-white mb-1">{employee.full_name}</h3>
                          <p className="text-sm text-slate-400 mb-4">{employee.email}</p>
                          <div className="space-y-2 mb-4">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Dossiers Actifs:</span>
                              <span className="font-semibold text-white">{activeCases}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Terminés:</span>
                              <span className="font-semibold text-green-400">{completedCases}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Total Dossiers:</span>
                              <span className="font-semibold text-white">{employeeCases.length}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases">
            <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Tous les Dossiers</CardTitle>
                <CardDescription className="text-slate-400">Vue complète de tous les dossiers d'immigration</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Recherche et Tri */}
                <SearchAndSort
                  data={cases}
                  searchFields={['client_name', 'country', 'visa_type', 'status']}
                  sortOptions={[
                    { value: 'created_at', label: 'Date de création' },
                    { value: 'client_name', label: 'Nom du client' },
                    { value: 'country', label: 'Pays' },
                    { value: 'status', label: 'Statut' },
                    { value: 'progress_percentage', label: 'Progression' }
                  ]}
                  onFilteredDataChange={setFilteredCases}
                  placeholder="Rechercher un dossier (client, pays, visa, statut)..."
                />

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCases.map((caseItem) => {
                    const caseTotalSteps = getTotalSteps(caseItem, caseItem.country, caseItem.visa_type);
                    const caseStepIndex = caseItem.current_step_index || 0;
                    const caseSafePercentage = Math.round(((caseStepIndex + 1) / caseTotalSteps) * 100);

                    return (
                      <Card key={caseItem.id} className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-2 border-slate-700 hover:border-orange-500 transition-all">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center border border-orange-500/20">
                              <Briefcase className="w-5 h-5 text-orange-400" />
                            </div>
                            <Badge className={getStatusColor(caseItem.status)}>{caseItem.status}</Badge>
                          </div>
                          <h3 className="font-bold text-lg text-white mb-1">{caseItem.client_name}</h3>
                          <p className="text-sm text-slate-400 mb-3">{caseItem.country} - {caseItem.visa_type}</p>

                          {/* Progress Bar */}
                          <div className="mb-3">
                            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                              <span>Étape {caseStepIndex + 1} sur {caseTotalSteps}</span>
                              <span className="font-medium text-orange-400">{caseSafePercentage}%</span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-orange-500 to-orange-600 h-full transition-all"
                                style={{ width: `${caseSafePercentage}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2 mb-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Étape actuelle:</span>
                              <span className="font-semibold text-slate-300 text-xs truncate ml-2">{caseItem.workflow_steps?.[caseStepIndex]?.title || '—'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Créé le:</span>
                              <span className="font-semibold text-white text-xs">{new Date(caseItem.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-xs">
                                <Settings className="w-3 h-3 mr-1" />
                                Mise à jour
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#1E293B] border-slate-700">
                              <DialogHeader>
                                <DialogTitle className="text-white">Mise à jour du Dossier</DialogTitle>
                                <DialogDescription className="text-slate-400">
                                  Mettre à jour le statut et la progression de {caseItem.client_name}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label className="text-slate-300">Étape actuelle</Label>
                                  <Select 
                                    value={caseItem.current_step_index.toString()}
                                    onValueChange={(value) => handleUpdateCase(caseItem.id, { current_step_index: parseInt(value) })}
                                  >
                                    <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#1E293B] border-slate-600">
                                      {(caseItem.workflow_steps || []).map((step, idx) => (
                                        <SelectItem key={idx} value={idx.toString()} className="text-white hover:bg-slate-700">
                                          Étape {idx + 1}: {step.title}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-slate-300">Statut</Label>
                                  <Select 
                                    value={caseItem.status}
                                    onValueChange={(value) => handleUpdateCase(caseItem.id, { status: value })}
                                  >
                                    <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#1E293B] border-slate-600">
                                      <SelectItem value="Nouveau" className="text-white hover:bg-slate-700">Nouveau</SelectItem>
                                      <SelectItem value="En cours" className="text-white hover:bg-slate-700">En cours</SelectItem>
                                      <SelectItem value="En attente" className="text-white hover:bg-slate-700">En attente</SelectItem>
                                      <SelectItem value="Approuvé" className="text-white hover:bg-slate-700">Approuvé</SelectItem>
                                      <SelectItem value="Terminé" className="text-white hover:bg-slate-700">Terminé</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visitors Tab */}
          <TabsContent value="visitors">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Enregistrer un Visiteur</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Nom complet */}
                    <div>
                      <Label className="text-slate-300 font-medium">Nom complet du visiteur *</Label>
                      <Input
                        value={newVisitor.full_name}
                        onChange={(e) => setNewVisitor({ ...newVisitor, full_name: e.target.value })}
                        placeholder="Ex: Jean Dupont"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 mt-2"
                        data-testid="visitor-name-input"
                        required
                      />
                    </div>

                    {/* Numéro de téléphone */}
                    <div>
                      <Label className="text-slate-300 font-medium">Numéro de téléphone *</Label>
                      <Input
                        value={newVisitor.phone_number}
                        onChange={(e) => setNewVisitor({ ...newVisitor, phone_number: e.target.value })}
                        placeholder="+237 6XX XX XX XX"
                        type="tel"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 mt-2"
                        data-testid="visitor-phone-input"
                        required
                      />
                    </div>

                    {/* Numéro de CNI */}
                    <div>
                      <Label className="text-slate-300 font-medium">Numéro de CNI *</Label>
                      <Input
                        value={newVisitor.cni_number}
                        onChange={(e) => setNewVisitor({ ...newVisitor, cni_number: e.target.value })}
                        placeholder="Ex: 123456789"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 mt-2"
                        data-testid="visitor-cni-input"
                        required
                      />
                    </div>

                    {/* Motif */}
                    <div>
                      <Label className="text-slate-300 font-medium">Motif de la visite *</Label>
                      <Select value={newVisitor.purpose} onValueChange={(value) => setNewVisitor({ ...newVisitor, purpose: value })}>
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-slate-600">
                          <SelectItem value="Consultation initiale" className="text-white hover:bg-slate-700">Consultation initiale</SelectItem>
                          <SelectItem value="Suivi de dossier" className="text-white hover:bg-slate-700">Suivi de dossier</SelectItem>
                          <SelectItem value="Dépôt de documents" className="text-white hover:bg-slate-700">Dépôt de documents</SelectItem>
                          <SelectItem value="Rendez-vous" className="text-white hover:bg-slate-700">Rendez-vous</SelectItem>
                          <SelectItem value="Autre" className="text-white hover:bg-slate-700">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Other purpose (conditional) */}
                    {newVisitor.purpose === 'Autre' && (
                      <div>
                        <Label className="text-slate-300 font-medium">Précisez le motif</Label>
                        <Input
                          value={newVisitor.other_purpose}
                          onChange={(e) => setNewVisitor({ ...newVisitor, other_purpose: e.target.value })}
                          placeholder="Décrivez le motif de la visite"
                          className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 mt-2"
                        />
                      </div>
                    )}

                    <Button onClick={handleAddVisitor} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/50" data-testid="add-visitor-btn">
                      <Building2 className="w-4 h-4 mr-2" />
                      Enregistrer le Visiteur
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Liste des Visiteurs</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Recherche et Tri */}
                  <SearchAndSort
                    data={visitors}
                    searchFields={['full_name', 'phone_number', 'purpose', 'cni_number', 'registered_by']}
                    sortOptions={[
                      { value: 'arrival_time', label: 'Date d\'arrivée' },
                      { value: 'full_name', label: 'Nom' },
                      { value: 'purpose', label: 'Motif' }
                    ]}
                    onFilteredDataChange={setFilteredVisitors}
                    placeholder="Rechercher un visiteur (nom, téléphone, motif, CNI)..."
                  />

                  <div className="space-y-3">
                    {filteredVisitors.slice(0, 10).map((visitor) => (
                      <div key={visitor.id} className="flex items-center justify-between p-4 bg-[#0F172A] border border-slate-700 rounded-lg hover:border-orange-500/50 transition-all">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-bold text-white text-lg">{visitor.full_name}</p>
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              📱 {visitor.phone_number}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400 mb-1">
                            <span className="font-semibold">Motif:</span> {visitor.purpose}
                            {visitor.other_purpose && ` - ${visitor.other_purpose}`}
                          </p>
                          <p className="text-xs text-slate-500 mb-1">
                            <span className="font-semibold">CNI:</span> {visitor.cni_number}
                          </p>
                          <p className="text-xs text-slate-500">
                            <span className="font-semibold">Arrivée:</span> {new Date(visitor.arrival_time).toLocaleString('fr-FR')}
                          </p>
                          {visitor.registered_by && (
                            <p className="text-xs text-slate-600 mt-1">
                              Enregistré par: {visitor.registered_by}
                            </p>
                          )}
                        </div>
                        <div className="ml-4">
                          {!visitor.departure_time && (
                            <Button variant="outline" size="sm" onClick={() => handleCheckoutVisitor(visitor.id)} className="border-slate-600 text-slate-300 hover:bg-orange-500 hover:text-white hover:border-orange-500">
                              Départ
                            </Button>
                          )}
                          {visitor.departure_time && (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              ✓ Parti
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Prospects Tab */}
          <TabsContent value="prospects">
            <MyProspects />
          </TabsContent>

        </Tabs>
      </div>
      )}

      {/* Chat Widget */}
      <ChatWidget userRole="MANAGER" />

      {/* Reassign Client Dialog */}
      {reassignDialog.show && (
        <Dialog open={reassignDialog.show} onOpenChange={(open) => setReassignDialog({ ...reassignDialog, show: open })}>
          <DialogContent className="bg-[#1E293B] border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Réassigner le Client</DialogTitle>
              <DialogDescription className="text-slate-400">
                Sélectionnez le nouvel employé pour {reassignDialog.client?.client_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {employees.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-400">Chargement de la liste des employés...</p>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-slate-300">Nouvel Employé * ({employees.length} disponibles)</Label>
                    <select
                      value={reassignDialog.newEmployeeId}
                      onChange={(e) => setReassignDialog({ ...reassignDialog, newEmployeeId: e.target.value })}
                      className="w-full mt-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Choisir un employé</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleReassignClient}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                      disabled={!reassignDialog.newEmployeeId}
                    >
                      Réassigner
                    </Button>
                    <Button
                      onClick={() => setReassignDialog({ show: false, client: null, newEmployeeId: '' })}
                      variant="outline"
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      Annuler
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Popup Credentials Uniforme */}
      <CredentialsPopup
        open={showCredentialsDialog}
        onOpenChange={(open) => {
          setShowCredentialsDialog(open);
          if (!open) setNewClientCredentials(null);
        }}
        credentials={newClientCredentials}
      />

      {/* Dialog Créer un Employé */}
      <Dialog open={showCreateEmployee} onOpenChange={setShowCreateEmployee}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Créer un Nouvel Employé</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ajoutez un nouveau conseiller à votre équipe
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateEmployee} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nom Complet *</Label>
              <Input
                value={newEmployee.full_name}
                onChange={(e) => setNewEmployee({ ...newEmployee, full_name: e.target.value })}
                required
                placeholder="Ex: Marie Kouassi"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                required
                placeholder="email@example.com"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Téléphone *</Label>
              <Input
                value={newEmployee.phone}
                onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                required
                placeholder="+237 XXX XXX XXX"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-lg">
              <p className="text-green-400 text-sm">
                ℹ️ Un compte employé sera créé avec un mot de passe temporaire qui sera automatiquement envoyé par email.
              </p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Créer l'Employé
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Détails Client */}
      {selectedClient && (
        <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
          <DialogContent className="bg-[#1E293B] border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="relative">
              <button
                onClick={() => setSelectedClient(null)}
                className="absolute right-0 top-0 text-slate-400 hover:text-white transition-colors text-2xl font-bold leading-none"
                aria-label="Fermer"
              >
                ✕
              </button>
              <DialogTitle className="text-white flex items-center gap-2 pr-8">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                Détails du Client
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 sm:space-y-6 mt-3 sm:mt-4">
              {/* Informations de base */}
              <div className="bg-slate-800 p-3 sm:p-4 rounded-lg space-y-3">
                <h4 className="text-white font-semibold mb-3 text-sm sm:text-base">📋 Informations Personnelles</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Nom complet</p>
                    <p className="text-white font-medium text-sm sm:text-base break-words">{selectedClient.full_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Email</p>
                    <p className="text-white text-sm sm:text-base break-all">{selectedClient.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Téléphone</p>
                    <p className="text-white text-sm sm:text-base">{selectedClient.phone || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Statut</p>
                    <Badge className={`${selectedClient.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'} w-fit`}>
                      {selectedClient.status === 'active' ? 'Actif' : selectedClient.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Informations du dossier */}
              <div className="bg-slate-800 p-3 sm:p-4 rounded-lg space-y-3">
                <h4 className="text-white font-semibold mb-3 text-sm sm:text-base">🗂️ Dossier d'Immigration</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Pays de destination</p>
                    <p className="text-white font-medium text-sm sm:text-base">{selectedClient.country}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Type de visa</p>
                    <p className="text-white text-sm sm:text-base">{selectedClient.visa_type}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Employé assigné</p>
                    <p className="text-white text-sm sm:text-base break-words">{selectedClient.assigned_employee_name || 'Non assigné'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Date de création</p>
                    <p className="text-white text-sm sm:text-base">
                      {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Identifiants de connexion */}
              <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
                <h4 className="text-blue-400 font-semibold mb-3">🔑 Identifiants de Connexion</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Email de connexion</p>
                    <div className="flex items-center gap-2">
                      <code className="text-white bg-slate-900 px-3 py-2 rounded font-mono text-sm flex-1">
                        {selectedClient.email}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedClient.email);
                          toast.success('Email copié!');
                        }}
                      >
                        Copier
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Mot de passe par défaut</p>
                    <div className="flex items-center gap-2">
                      <code className="text-white bg-slate-900 px-3 py-2 rounded font-mono text-sm flex-1">
                        Aloria2024!
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600"
                        onClick={() => {
                          navigator.clipboard.writeText('Aloria2024!');
                          toast.success('Mot de passe copié!');
                        }}
                      >
                        Copier
                      </Button>
                    </div>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mt-2">
                    <p className="text-amber-200 text-xs flex items-start gap-2">
                      <span className="text-sm">⚠️</span>
                      <span>Le client devra changer ce mot de passe lors de sa première connexion.</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-3 sm:pt-4 border-t border-slate-700">
                <Button 
                  onClick={() => setSelectedClient(null)}
                  className="bg-slate-600 hover:bg-slate-500 w-full sm:w-auto"
                >
                  Fermer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}
